import { useState } from "react";
import { Button } from "../../primitives/Button";
import {
  publishProcessTemplate,
  archiveProcessTemplate,
  deleteProcessTemplate,
  cloneProcessTemplate,
  activateProcessTemplate,
  type ProcessTemplate,
} from "../../../api/processes";

type ProcessTemplateActionsProps = {
  template: ProcessTemplate;
  onUpdate: () => void;
  /** Appelé quand le processus a été supprimé définitivement (pour rafraîchir la liste et effacer la sélection). */
  onTemplateDeleted?: () => void;
};

export function ProcessTemplateActions({ template, onUpdate, onTemplateDeleted }: ProcessTemplateActionsProps) {
  const [isPublishing, setIsPublishing] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const handlePublish = async () => {
    if (!confirm("Êtes-vous sûr de vouloir publier ce processus ? Cette action est irréversible.")) {
      return;
    }
    setIsPublishing(true);
    try {
      await publishProcessTemplate(template.id);
      alert("Processus publié avec succès !");
      onUpdate();
    } catch (error: any) {
      alert(`Erreur lors de la publication: ${error.message || error}`);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleArchive = async () => {
    if (!confirm("Êtes-vous sûr de vouloir archiver ce processus ?")) {
      return;
    }
    setIsArchiving(true);
    try {
      await archiveProcessTemplate(template.id);
      alert("Processus archivé avec succès !");
      onUpdate();
    } catch (error: any) {
      alert(`Erreur lors de l'archivage: ${error.message || error}`);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        "Supprimer ce processus ? S'il est utilisé (lié à un produit ou à des dossiers), il sera archivé à la place."
      )
    ) {
      return;
    }
    setIsDeleting(true);
    try {
      const result = await deleteProcessTemplate(template.id);
      if (result.deleted) {
        alert("Processus supprimé définitivement.");
        onTemplateDeleted?.();
      } else {
        alert("Processus archivé (il est encore utilisé par des produits ou des dossiers).");
        onUpdate();
      }
    } catch (error: any) {
      alert(`Erreur: ${error.message || error}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClone = async () => {
    setIsCloning(true);
    try {
      await cloneProcessTemplate(template.id, {});
      alert("Processus cloné avec succès !");
      onUpdate();
    } catch (error: any) {
      alert(`Erreur lors du clonage: ${error.message || error}`);
    } finally {
      setIsCloning(false);
    }
  };

  const handleActivate = async (activate: boolean) => {
    setIsActivating(true);
    try {
      await activateProcessTemplate(template.id, activate);
      alert(activate ? "Processus activé avec succès !" : "Processus désactivé avec succès !");
      onUpdate();
    } catch (error: any) {
      alert(`Erreur: ${error.message || error}`);
    } finally {
      setIsActivating(false);
    }
  };

  const canEdit = template.status === "draft";
  const isPublished = template.status === "published";
  const isArchived = template.status === "archived";

  return (
    <div className="flex gap-2">
      {canEdit && (
        <Button
          label={isPublishing ? "Publication..." : "Publier"}
          intent="primary"
          size="sm"
          onClick={handlePublish}
          state={isPublishing ? "loading" : "default"}
        />
      )}
      {isPublished && (
        <Button
          label={isActivating ? "Désactivation..." : "Désactiver"}
          intent="default"
          size="sm"
          onClick={() => handleActivate(false)}
          state={isActivating ? "loading" : "default"}
        />
      )}
      {!isArchived && (
        <>
          <Button
            label={isCloning ? "Clonage..." : "Cloner"}
            intent="default"
            size="sm"
            onClick={handleClone}
            state={isCloning ? "loading" : "default"}
          />
          <Button
            label={isArchiving ? "Archivage..." : "Archiver"}
            intent="default"
            size="sm"
            onClick={handleArchive}
            state={isArchiving ? "loading" : "default"}
          />
          <Button
            label={isDeleting ? "Suppression..." : "Supprimer"}
            intent="danger"
            size="sm"
            onClick={handleDelete}
            state={isDeleting ? "loading" : "default"}
          />
        </>
      )}
      {isArchived && (
        <Button
          label={isDeleting ? "Suppression..." : "Supprimer"}
          intent="danger"
          size="sm"
          onClick={handleDelete}
          state={isDeleting ? "loading" : "default"}
        />
      )}
    </div>
  );
}
